/**
 * The whole UI view (plan §6 M2-18): which screen shows, its view-models and its focus groups, from one store
 * slot). The React tree renders exactly this; the focus model navigates `groups`. Pure, but for the Outfitting fab rows
 * reading the registered fab world provider (`fabWorld.ts`, MC-c5b: Mk III geography, the shortage card, remote berths),
 * and the Repair and Contracts tabs reading its strategic signals (K12: `linked` for plating repairs, `view` for a
 * works site's live need).
 */
import { SLOT_IDS, type M2Data, type SlotId, type StationDef } from '../../data/m2Schemas'
import { UI_CONFIG } from '../config'
import type { ConfirmRequest, StationTab, UiState, UiStore } from '../contracts'
import type { FocusGroup } from '../nav'
import { contractsModel, type ContractsModel } from './contracts'
import { G, TITLE_ITEMS, confirmGroups, externalStationGroups, momentGroups, moreDrawerGroups, stationGroups, titleGroups, type TabCounts, type TitleView } from './layout'
import { fabWorldOptions } from './fabWorld'
import { leaseModel, type LeaseModel } from './lease'
import { marketModel, rowButtonIndex, sellAllOnTop, type MarketModel } from './market'
import { deathModel, ownershipModel, type DeathModel, type OwnershipModel } from './moments'
import { outfittingModel, salvagedButtonCount, slotOffers, type ModuleOffer, type OutfittingModel } from './outfitting'
import { repairModel, type RepairModel } from './repair'
import { savesModel, titleModel, type SavesModel, type TitleModel } from './saves'
import { dockedStation, effectiveTab, launchModel, stationHeader, visibleTabs, type LaunchModel, type StationHeaderModel } from './station'
import { barCount, barIndexOf, groupTabs, moreTabs, type TabGroups, type TabReachSignals } from './stationTabs'

export interface StationView {
  kind: 'station'
  station: StationDef
  header: StationHeaderModel
  tabs: StationTab[]
  tab: StationTab
  launch: LaunchModel
  market: MarketModel | null
  outfitting: { model: OutfittingModel; slot: SlotId; offers: ModuleOffer[] } | null
  repair: RepairModel | null
  lease: LeaseModel | null
  contracts: ContractsModel | null
  saves: SavesModel | null
  /** Tabs another module mounts after M2's (M3 R22f: the Reach's station services); empty without one. */
  external: ExternalTabInfo[]
  /** The external tab showing instead of `tab`, or null. */
  externalTab: string | null
  /** CL-5 (core-loop plan §4): `tabs` and `external` as the bar and the grouped More drawer (`stationTabs.ts groupTabs`). */
  tabGroups: TabGroups
}

/** An external tab as the tab bar lists it. */
export interface ExternalTabInfo {
  id: string
  title: string
}

/** The station screen's external tabs (UI-local state: which one shows, and how many buttons it has mounted). */
export interface ExternalView {
  tabs: readonly ExternalTabInfo[]
  /** The external tab the player picked (ignored when it is not in `tabs`). */
  active: string | null
  /** Buttons the active tab has mounted (its focus group). */
  buttons: number
  /** CL-5: the Reach's state the tab promotion rules read (chart data, fines or bounties, a corporation); absent = none. */
  signals?: TabReachSignals | null
  /** CL-6: the More drawer is open (UI-local); absent = closed. */
  moreOpen?: boolean
}

/** CL-6: the showing tab's id in the tab groups (M2's tab by name, an external one as `ext-<id>`). */
export function activeTabId(v: Pick<StationView, 'tab' | 'externalTab'>): string {
  return v.externalTab === null ? v.tab : `ext-${v.externalTab}`
}

const NO_EXTERNAL: ExternalView = Object.freeze({ tabs: [], active: null, buttons: 0 })

export type ScreenView =
  | { kind: 'none' }
  | { kind: 'title'; view: TitleView; title: TitleModel }
  | StationView
  | { kind: 'death'; death: DeathModel }
  | { kind: 'ownership'; ownership: OwnershipModel }

export interface UiView {
  screen: ScreenView
  /** A save is loading (the load hold): the screen stays drawn but takes no input (keys, pad, clicks). */
  inputLocked: boolean
  /** A confirmation on top of the screen (it takes the focus). */
  confirm: ConfirmRequest | null
  groups: FocusGroup[]
  /** Changes whenever the focus should start over (screen, tab, title view, confirm). */
  contextKey: string
  /** The group a fresh focus starts in. */
  preferred: string
  /** Focus memory a fresh focus starts with (the tab bar remembers the active tab). */
  seed: Record<string, number>
  /** The item of `preferred` a fresh focus starts on (CL-10: New career on a title with nothing to continue). */
  start: number
}

/** The outfitting slot the focus memory selects. */
export function selectedSlot(memory: Readonly<Record<string, number>>): SlotId {
  const i = memory[G.slots] ?? 0
  return SLOT_IDS[i < 0 ? 0 : i >= SLOT_IDS.length ? SLOT_IDS.length - 1 : i]
}

const NONE: ScreenView = { kind: 'none' }

function stationView(data: M2Data, state: UiState, memory: Readonly<Record<string, number>>): { view: StationView; counts: TabCounts } | null {
  const career = state.career
  const station = dockedStation(data, state)
  const header = stationHeader(data, state)
  if (career === null || station === null || header === null) return null
  const tabs = visibleTabs(station)
  const tab = effectiveTab(tabs, state.tab)
  const view: StationView = {
    kind: 'station',
    station,
    header,
    tabs,
    tab,
    launch: launchModel(state),
    market: null,
    outfitting: null,
    repair: null,
    lease: null,
    contracts: null,
    saves: null,
    external: [],
    externalTab: null,
    tabGroups: { primary: [], more: [] },
  }
  const counts: TabCounts = {}
  switch (tab) {
    case 'market': {
      // The store's state carries the actions (SY-2c: the goods rows quote through `quoteGood`).
      const store: Partial<UiStore> = state
      const m = marketModel(data, career, state.market, station, UI_CONFIG, store.actions?.quoteGood, store.actions?.quoteGoodAt)
      // M10 P5a8a: the tender strip rides the market model only when the runtime set it (the key on).
      view.market = state.tender !== undefined ? { ...m, tender: state.tender } : m
      // Goods rows follow the ware rows in the sell and buy focus groups.
      // SY-3, SY-7: the row buttons (Break down, Deliver; wired actions only) form their own group.
      const actions = store.actions ?? {}
      const breakDown = rowButtonIndex(m.components, m.components.length, actions)
      counts.market = { sell: m.sell.length + m.goodsSell.length, buy: m.buy.length + m.goodsBuy.length, components: m.components.length, breakDown }
      // CL-4: the focus order follows the screen (Market.tsx): Buy first per `firstSection`, Sell all on top while carrying.
      if (m.firstSection === 'buy') counts.market.buyFirst = true
      if (sellAllOnTop(m)) counts.market.sellAllTop = true
      // M10 P5a3c: Send tender joins the Sell all group while the strip shows (keys off: absent, today's count).
      if (state.tender !== undefined) counts.market.tender = true
      break
    }
    case 'outfitting': {
      const slot = selectedSlot(memory)
      const offers = slotOffers(data, career, station, slot, fabWorldOptions(data, state.gameTime))
      const model = outfittingModel(data, career, station)
      view.outfitting = { model, slot, offers }
      // SY-5c: the Salvaged buttons, then the Recondition buttons, then the Scrap buttons (MC-c7) form one focus group below the slots.
      counts.outfitting = { offers: offers.length, salvaged: salvagedButtonCount(model.salvaged) + model.recondition.length + model.scrap.length }
      break
    }
    case 'repair': {
      // SNK-3b: a station the strategic economy prices repairs in plating even where none is quoted (refinery, habitat).
      const linked = fabWorldOptions(data, state.gameTime).linked
      view.repair = repairModel(data, career, linked === undefined ? null : linked(station.id))
      break
    }
    case 'lease':
      view.lease = leaseModel(data, career, state.gameTime)
      counts.lease = { owned: career.lease.owned }
      break
    case 'contracts': {
      // SNK-6b2: a works hand-in is checked against the site's live need (the local view's row).
      const c = contractsModel(data, career, station, state.gameTime, UI_CONFIG, fabWorldOptions(data, state.gameTime).view ?? null)
      view.contracts = c
      counts.contracts = { board: c.board.length, active: c.active.length }
      break
    }
    case 'saves':
      view.saves = savesModel(data, state)
      break
  }
  return { view, counts }
}

/** The first content group of a tab (where a fresh station focus starts). */
function firstContentGroup(groups: readonly FocusGroup[]): string {
  const content = groups.filter((g) => g.id !== G.tabs && g.id !== G.notices && g.id !== G.footer && g.count > 0)
  // CL-4: Sell all on top of the Market is never the first focus (Enter on open would sell the hold); its rows are.
  return (content.find((g) => g.id !== G.sellAll) ?? content[0])?.id ?? G.tabs
}

export function buildView(
  data: M2Data,
  state: UiState,
  titleView: TitleView,
  memory: Readonly<Record<string, number>>,
  external: ExternalView = NO_EXTERNAL,
): UiView {
  const confirm = state.confirm
  let screen: ScreenView = NONE
  let groups: FocusGroup[] = []
  let preferred = ''
  let start = 0
  let key: string = state.screen
  const seed: Record<string, number> = {}

  switch (state.screen) {
    case 'title': {
      const title = titleModel(data, state)
      screen = { kind: 'title', view: titleView, title }
      groups = titleGroups(titleView, title.rows.length, title.anyBad && title.fallback !== null)
      preferred = titleView === 'main' ? G.title : G.load
      // CL-10: with nothing to continue (Continue is disabled) the focus starts on New career, so Enter / pad A starts one.
      if (titleView === 'main') start = TITLE_ITEMS.indexOf(title.continueSlot === null ? 'new' : 'continue')
      // CL-10: the save list can arrive after the title shows; the focus starts over when Continue comes or goes.
      key = titleView === 'main' && title.continueSlot === null ? 'title:main:fresh' : `title:${titleView}`
      break
    }
    case 'station': {
      const s = stationView(data, state, memory)
      if (s !== null) {
        const v = s.view
        v.external = external.tabs.map((t) => ({ id: t.id, title: t.title }))
        if (state.career !== null) v.tabGroups = groupTabs(v, state.career, external.signals ?? null)
        const ext = external.active === null ? -1 : v.external.findIndex((t) => t.id === external.active)
        // CL-6: the bar holds the primary tabs plus the More button; the drawer's tabs are their own group.
        const bar = barCount(v.tabGroups)
        screen = v
        if (ext >= 0) {
          v.externalTab = v.external[ext].id
          groups = externalStationGroups(bar, external.buttons, state.notices.length)
          key = `station:${state.stationId ?? ''}:ext:${v.externalTab}`
        } else {
          groups = stationGroups(bar, v.tab, s.counts, state.notices.length)
          key = `station:${state.stationId ?? ''}:${v.tab}`
        }
        const active = activeTabId(v)
        seed[G.tabs] = barIndexOf(v.tabGroups, active)
        preferred = firstContentGroup(groups)
        const drawer = moreTabs(v.tabGroups)
        if (external.moreOpen === true && drawer.length > 0) {
          groups = moreDrawerGroups(bar, drawer.length)
          key = `${key}:more`
          seed[G.more] = Math.max(0, drawer.findIndex((t) => t.id === active))
          preferred = G.more
        }
      }
      break
    }
    case 'death':
      if (state.deathReport !== null) {
        screen = { kind: 'death', death: deathModel(data, state.deathReport) }
        groups = momentGroups()
        preferred = G.moment
      }
      break
    case 'ownership':
      if (state.career !== null) {
        screen = { kind: 'ownership', ownership: ownershipModel(state.career) }
        groups = momentGroups()
        preferred = G.moment
      }
      break
    case 'none':
      break
  }

  if (confirm !== null && screen.kind !== 'none') {
    return { screen, inputLocked: state.loading, confirm, groups: confirmGroups(), contextKey: `${key}|confirm:${confirm.id}`, preferred: G.confirm, seed: {}, start: 0 }
  }
  return { screen, inputLocked: state.loading, confirm: null, groups, contextKey: key, preferred, seed, start }
}
